import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Subject, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, catchError, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { GridConfig } from '../../../v2/models/shared/grid/grid.model';
import { RoleplayFeedbackApiService } from '../../../v2/services/roleplay-feedback/roleplay-feedback-api.service';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';
import { UserService } from '../../../service/shared/user-service.service';

@Component({
    selector: 'app-roleplay-feedback-list',
    templateUrl: './roleplay-feedback-list.component.html',
    styleUrl: './roleplay-feedback-list.component.scss',
    standalone: false
})
export class RoleplayFeedbackListComponent implements OnInit, OnDestroy {

    private destroy$ = new Subject<void>();
    private pageChange$ = new Subject<void>();
    private tabChange$ = new Subject<number>();

    filteredList: any[] = [];
    totalCount: number = 0;
    currentPage: number = 0;
    pageSize: number = 5;
    pageSizeOptions: number[] = [5, 10, 25, 50];
    selectedTabIndex: number = 0;

    canViewAllFeedbackTab = false;
    canViewAssignedFeedbackTab = false;

    searchFilter = new FormControl('');

    gridConfig: GridConfig = {
        rowClickable: true,
        hidePaginator: true
    };

    columns = [
        { label: 'Session ID', key: 'chat_session_id', type: 'default' },
        { label: 'Session Objective', key: 'session_objective', type: 'default' },
        { label: 'Rating', key: 'rating', type: 'default' },
        { label: 'Status', key: 'feedback_status', type: 'default' },
        { label: 'Assignee', key: 'assigned_category', type: 'default' },
        { label: 'Submitted By', key: 'given_by_name', type: 'default' },
        { label: 'Date', key: 'created_date', type: 'date', date_format: 'd MMM yyyy h:mma' }
    ];

    constructor(private userService: UserService,
        private router: Router,
        private roleplayFeedbackApiService: RoleplayFeedbackApiService,
        private contactCenterService: ContactCenterApiService
    ) { }

    ngOnInit(): void {
        this.initializeTabVisibility()
        const searchChange$ = this.searchFilter.valueChanges.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            tap(() => this.currentPage = 0),
            takeUntil(this.destroy$)
        );

        merge(searchChange$, this.pageChange$, this.tabChange$.pipe(tap(() => this.currentPage = 0))).pipe(
            startWith(null),
            switchMap(() => {
                this.showLoader();
                const search = (this.searchFilter.value || '').trim();
                const offset = this.currentPage * this.pageSize;

                if (this.selectedTabIndex === 1) {
                    const userId = sessionStorage.getItem('userId');
                    const businessUnitCode = sessionStorage.getItem('businessUnitCode') || '';
                    return this.contactCenterService.getRoleplayFeedbacksByCategory(Number(userId), businessUnitCode).pipe(
                        catchError(() => { this.hideLoader(); return of([]); })
                    );
                }

                return this.roleplayFeedbackApiService.fetchRoleplayFeedbackWithLogsByUser({
                    business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
                    search: search || undefined,
                    limit: this.pageSize,
                    offset: offset
                }).pipe(
                    catchError(() => { this.hideLoader(); return of([]); })
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (data) => {
                const feedbacks: any[] = data?.results || data?.roleplay_feedbacks || (Array.isArray(data) ? data : (data?.data || data?.feedbacks || []));
                this.totalCount = data?.total_count || feedbacks.length;
                this.filteredList = feedbacks.map(f => ({
                    ...f,
                    session_objective: f.session_objective || f._objective || '',
                    given_by_name: [f.given_by_first_name, f.given_by_last_name].filter(Boolean).join(' ') || `User ${f.given_by}`,
                    assigned_category: f.assigned_category || 'Unassigned',
                    created_date: this.convertUtcToLocal(f.created_date)
                }));
                this.hideLoader();
            },
            error: () => {
                this.hideLoader();
            }
        });
    }

    private initializeTabVisibility(): void {
        this.canViewAllFeedbackTab = this.userService.isSuperVisor() || this.userService.isTrainerUser();
        this.canViewAssignedFeedbackTab = true
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPageChange(event: PageEvent): void {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        this.pageChange$.next();
    }

    onTabChange(event: any): void {
        this.selectedTabIndex = event.index;
        this.searchFilter.setValue('', { emitEvent: false });
        this.currentPage = 0;
        this.tabChange$.next(event.index);
    }

    onRowClick(row: any): void {
        this.router.navigate([`app-cc-roleplay-feedback-detail/${row.id}`], {
            skipLocationChange: true,
            state: { feedbackRow: row }
        });
    }

    private showLoader(): void {
        const el = document.getElementById('rp-feedback-loader');
        if (el) el.style.display = 'inline-block';
    }

    private hideLoader(): void {
        const el = document.getElementById('rp-feedback-loader');
        if (el) el.style.display = 'none';
    }

    private convertUtcToLocal(utcDateString: string): Date | null {
        if (!utcDateString || typeof utcDateString !== 'string') return null;
        const utcDate = utcDateString.includes('.')
            ? utcDateString.split('.')[0] + 'Z'
            : utcDateString.endsWith('Z') ? utcDateString : utcDateString + 'Z';
        const date = new Date(utcDate);
        return isNaN(date.getTime()) ? null : date;
    }
}
